import { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import {
    getUserEventSocialShareLinks,
    GetUserEventSocialShareLinksOutput,
} from "../actions";
import { APP_URL } from "~/utils/constants";

export const useShareFunctions = (
    eventId: string | undefined,
    useUserUploadedImage: boolean = false,
) => {
    const [shareLinks, setShareLinks] =
        useState<GetUserEventSocialShareLinksOutput | null>(null);

    useEffect(() => {
        if (eventId) {
            getUserEventSocialShareLinks(eventId, useUserUploadedImage)
                .then(setShareLinks)
                .catch(console.error);
        }
    }, [eventId]);

    const copyToClipboardWithToast = (text: string, platform: string) => {
        if (!navigator) throw new Error("Navigator not found");
        navigator.clipboard.writeText(text);
        toast.success(`Copied ${platform} share link to clipboard!`, {
            autoClose: 500,
        });
    };

    const shareToX = useCallback(
        (message?: string) => {
            if (!shareLinks?.x) return;

            const shareLink = message
                ? `https://twitter.com/intent/tweet?text=${encodeURIComponent(message)}&url=${encodeURIComponent(shareLinks.eventUrl)}`
                : shareLinks.x.url;

            window.open(shareLink, "_blank");
        },
        [shareLinks?.x, shareLinks?.eventUrl],
    );

    const shareToInstagram = () =>
        shareLinks?.instagram &&
        copyToClipboardWithToast(shareLinks.instagram.url, "Instagram");

    const shareToFacebook = (message?: string) => {
        if (message) navigator.clipboard.writeText(message);
        shareLinks?.facebook && window.open(shareLinks.facebook.url, "_blank");
    };

    const shareToLinkedIn = (message?: string) => {
        // Prefer the LinkedIn-specific URL if available
        if (!shareLinks?.linkedin?.url) {
            // Log an error for debugging; you could also fall back to eventUrl
            console.error("LinkedIn share URL not available");
            // Optional fallback (uncomment if linkedin.url isn’t guaranteed):
            // if (!shareLinks?.eventUrl) return;
            // const linkedInShareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareLinks.eventUrl)}`;
            // if (message) copyMessageToClipboardWithToast(message);
            // window.open(linkedInShareUrl, '_blank');
            return;
        }

        if (message) copyToClipboardWithToast(message, "LinkedIn");
        window.open(shareLinks.linkedin.url, "_blank");
    };

    // const shareToWhatsApp = () =>
    //     shareLinks?.whatsapp && window.open(shareLinks.whatsapp.url, "_blank");

    // const shareToTelegram = () =>
    //     shareLinks?.telegram && window.open(shareLinks.telegram.url, "_blank");

    // const shareToDiscord = () =>
    //     shareLinks?.discord &&
    //     copyToClipboardWithToast(shareLinks.discord.url, "Discord");

    return {
        shareLinks,
        copyToClipboardWithToast,
        shareToX,
        shareToInstagram,
        shareToFacebook,
        shareToLinkedIn,
        // shareToWhatsApp,
        // shareToTelegram,
        // shareToDiscord,
    };
};

export const useShareCommunityFunctions = (
    communitySlug: string | undefined,
) => {
    const defaultMessage = "Join us on River!";
    const communityLink = `${APP_URL}/${communitySlug}`;

    const shareToX = (message = defaultMessage) => {
        const shareLink = `https://twitter.com/intent/tweet?text=${encodeURIComponent(message)}&url=${encodeURIComponent(communityLink)}`;
        window.open(shareLink, "_blank");
    };

    const shareToLinkedIn = (message = defaultMessage) => {
        const shareLink = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(communityLink)}&text=${encodeURIComponent(message)}`;
        window.open(shareLink, "_blank");
    };

    const shareToInstagram = (message = defaultMessage) => {
        // Instagram doesn't support direct message prefilling; message must be added manually
        const shareLink = `https://www.instagram.com/?url=${encodeURIComponent(communityLink)}`;
        window.open(shareLink, "_blank");
    };

    const shareToFacebook = (message = defaultMessage) => {
        const shareLink = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(communityLink)}&quote=${encodeURIComponent(message)}`;
        window.open(shareLink, "_blank");
    };

    return {
        shareToX,
        shareToLinkedIn,
        shareToInstagram,
        shareToFacebook,
    };
};
